import { useWeb3React } from '@web3-react/core'
import jwtDecode from 'jwt-decode'
import { useCallback } from 'react'
import { useDispatch } from 'react-redux'
import { setUserSignedState } from 'state/user/actions'

export type UserStatePayload = {
  address: string
  exp: number
  has_kyced: boolean
  totp_validated?: boolean
  roles: string[]
}

const useSignOAuth = () => {
  const { account, library } = useWeb3React()
  const dispatch = useDispatch()

  const NONCE_ENDPOINT = `${process.env.REACT_APP_BACKEND_URL}/api/backend-service/oauth/nonce`
  const AUTH_ENDPOINT = `${process.env.REACT_APP_BACKEND_URL}/api/backend-service/oauth/auth/if`

  const handleAuthenticate = useCallback(
    ({
      publicAddress: address,
      signature,
    }: {
      publicAddress: string
      signature: string
    }) =>
      fetch(`${AUTH_ENDPOINT}`, {
        body: JSON.stringify({ address, signature }),
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        method: 'POST',
      }).then((response) => response.json()),
    [AUTH_ENDPOINT],
  )

  const handleSignMessage = useCallback(
    async ({
      publicAddress,
      nonce,
    }: {
      publicAddress: string
      nonce: string
    }) => {
      try {
        const signature = await library?.send('personal_sign', [
          `SSO login with wallet, code: ${nonce}`,
          publicAddress,
        ])
        return { publicAddress, signature: signature?.result }
      } catch (err) {
        console.error(err)
        throw new Error('You need to sign the message to be able to log in.')
      }
    },
    [library],
  )

  const handleStorePersistent = useCallback(
    (response: any) => {
      const token = response?.data?.access_token
      const decoded = jwtDecode<UserStatePayload>(token)

      if (decoded) {
        const { address, roles, totp_validated = false } = decoded
        // set the expiry to 6.5 days ahead
        dispatch(
          setUserSignedState({
            isSigned: true,
            expiry: new Date().getTime() + 6.5 * 24 * 3600 * 1000,
            account: address,
            roles,
            isTotpValidated: totp_validated,
          }),
        )
      }
    },
    [dispatch],
  )

  const signOAuth = useCallback(
    (onSucceed?: any, onFailure?: any) => {
      if (!account) return
      // Fetch nonce
      const publicAddress = account.toLowerCase()
      fetch(`${NONCE_ENDPOINT}?address=${publicAddress}`)
        .then((res) => res.json())
        .then((res) => res.data?.nonce)
        // Popup MetaMask confirmation modal to sign message
        .then((nonce) => {
          if (!nonce) {
            throw new Error('No nonce found.')
          }
          return handleSignMessage({ publicAddress, nonce })
        })
        // Send signature to backend on the /auth route
        .then(handleAuthenticate)
        .then(handleStorePersistent)
        .then(onSucceed)
        .catch((error) => {
          console.error(`OAuth(if) Login failed:`, error)
          if (onFailure) onFailure()
        })
    },
    [
      account,
      NONCE_ENDPOINT,
      handleAuthenticate,
      handleSignMessage,
      handleStorePersistent,
    ],
  )

  return { signOAuth }
}

export default useSignOAuth
